import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { adminReports } from '@/api/admin';
import type { AssessmentDimensionResult, ProfitShareAssessmentDay, ProfitShareDeal } from '@/api/extras';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/widgets/Pill';
import { fmtDateTime, fmtMoney, moneyToNumber } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { cn } from '@/lib/utils';
import { PayoutEditor } from './PayoutEditor';

/**
 * 个人当月结算核对弹窗:**顶部考核记录 + 下方逐笔交易明细**,底部发放决定。
 *
 * 汇总数字取报表口径(后端与月度分润表同一函数),逐笔仅用于核对与展示 ——
 * 「先 SUM 再换算」与「逐笔换算」在极端舍入下可能差几分,金额一律以汇总为准。
 */

const DIM_LABEL: Record<AssessmentDimensionResult['type'], string> = {
  tradeCount: '笔数',
  profit: '盈利',
  loss: '亏损',
};

function DimensionPill({ d }: { d: AssessmentDimensionResult }) {
  const label = DIM_LABEL[d.type];
  const text =
    d.type === 'tradeCount' ? `${label} ${d.currentCount ?? 0}/${d.targetCount ?? 0}` : `${label} ${d.met ? '✓' : '✗'}`;
  const title =
    d.type === 'tradeCount'
      ? `当日开仓 ${d.currentCount ?? 0} 笔 / 目标 ${d.targetCount ?? 0} 笔`
      : d.type === 'profit'
        ? `当日盈利 ${d.currentValue ? fmtMoney(d.currentValue) : '—'} / 目标 ≥ ${d.targetValue ? fmtMoney(d.targetValue) : '—'}`
        : `当日亏损 ${d.currentValue ? fmtMoney(d.currentValue) : '—'} / 上限 ≤ ${d.targetValue ? fmtMoney(d.targetValue) : '—'}`;
  return (
    <span title={title}>
      <Pill tone={d.met ? 'up' : 'down'}>{text}</Pill>
    </span>
  );
}

export function UserSettlementDialog({
  userId,
  year,
  month,
  onClose,
}: {
  userId: string | null;
  year: number;
  month: number;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['admin', 'profit-share-user', userId, year, month],
    queryFn: () => adminReports.profitShareMonthlyUser(userId as string, year, month),
    enabled: !!userId,
  });
  const d = q.data;

  const exportDeals = () => {
    if (!d) return;
    downloadCsv(`settlement-${d.user.userId}-${year}-${String(month).padStart(2, '0')}`, d.deals, [
      { key: 'closedAt', header: '结算时间', format: (_, r: ProfitShareDeal) => fmtDateTime(r.closedAt) },
      { key: 'openedAt', header: '开仓时间', format: (_, r: ProfitShareDeal) => (r.openedAt ? fmtDateTime(r.openedAt) : '') },
      { key: 'symbolName', header: '品种', format: (_, r: ProfitShareDeal) => r.symbolName ?? r.symbolId },
      { key: 'side', header: '方向', format: (_, r: ProfitShareDeal) => (r.side === 'BUY' ? '买入' : '卖出') },
      { key: 'volume', header: '手数', format: (_, r: ProfitShareDeal) => (r.volume / 100).toFixed(2) },
      { key: 'entryPrice', header: '开仓价', format: (_, r: ProfitShareDeal) => (r.entryPrice != null ? String(r.entryPrice) : '') },
      { key: 'closePrice', header: '平仓价', format: (_, r: ProfitShareDeal) => (r.closePrice != null ? String(r.closePrice) : '') },
      { key: 'grossPnl', header: '毛盈亏', format: (_, r: ProfitShareDeal) => moneyToNumber(r.grossPnl).toFixed(2) },
      { key: 'serviceFee', header: '交易服务费', format: (_, r: ProfitShareDeal) => moneyToNumber(r.serviceFee).toFixed(2) },
      { key: 'netPnl', header: '净额', format: (_, r: ProfitShareDeal) => moneyToNumber(r.netPnl).toFixed(2) },
    ]);
  };

  return (
    <Dialog open={!!userId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {d ? `${d.user.nickname || d.user.phone || `用户${d.user.userId}`} · ${year} 年 ${month} 月 结算核对` : '结算核对'}
          </DialogTitle>
          {/*
            本端点恒全量(按 userId 直查),列表页默认口径下看不见的人从这里照样打得开 ——
            所以「为什么列表里没有他」的答案只能在这行给:归档 / 渠道停用是两个独立原因,
            只标「已归档」会让"人还在岗、只是渠道停用"的那批人看起来毫无异常。
          */}
          <DialogDescription>
            {d
              ? `用户ID ${d.user.userId}${d.user.phone ? ` · ${d.user.phone}` : ''} · 渠道 ${d.user.channelAlias || d.user.inviteCode || '—'}${d.user.archived ? ' · 已归档' : ''}${d.user.channelDisabled ? ' · 渠道已停用' : ''}`
              : '加载中…'}
          </DialogDescription>
        </DialogHeader>

        {q.isLoading && <p className="py-8 text-center text-sm text-ink-3">加载中…</p>}
        {q.isError && <p className="py-8 text-center text-sm text-down">加载失败:{(q.error as Error)?.message}</p>}

        {d && (
          <div className="space-y-5">
            {/* 汇总 */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="已实现净(扣费后)" value={fmtMoney(d.summary.realizedNet, { sign: true })} tone={moneyToNumber(d.summary.realizedNet) >= 0 ? 'up' : 'down'} />
              <Metric label="应分润(10%)" value={fmtMoney(d.summary.share)} tone="brand" />
              <Metric label="交易服务费" value={fmtMoney(d.summary.serviceFee)} />
              <Metric
                label="结算笔数"
                value={String(d.summary.settlements)}
                sub={`盈 ${d.summary.winCount} · 亏 ${d.summary.lossCount} · ${d.summary.tradeDays} 个交易日`}
              />
            </section>

            {/* 顶部:考核记录 */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-0">
                  考核记录
                  <span className="ml-2 text-xs font-normal text-ink-2 num">
                    达标 {d.assessment.passedDays}/{d.assessment.assessedDays}
                    {d.assessment.passRate != null && ` · ${(d.assessment.passRate * 100).toFixed(1)}%`}
                    {d.assessment.overriddenDays > 0 && ` · 已改判 ${d.assessment.overriddenDays} 天`}
                  </span>
                </h3>
                <span className="text-xs text-ink-3">未达标不影响分润金额,是否发放由人工判定</span>
              </div>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm">
                  <thead className="bg-bg-3 text-ink-2 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-normal">交易日</th>
                      <th className="px-3 py-2 text-right font-normal">交易笔数</th>
                      <th className="px-3 py-2 text-right font-normal">当日盈亏</th>
                      <th className="px-3 py-2 text-right font-normal">交易成本</th>
                      <th className="px-3 py-2 text-left font-normal">维度达标</th>
                      <th className="px-3 py-2 text-left font-normal">最终结果</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.assessmentDays.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-ink-3">当月无考核记录</td>
                      </tr>
                    )}
                    {d.assessmentDays.map((a: ProfitShareAssessmentDay) => {
                      const pnl = moneyToNumber(a.realizedPnl);
                      return (
                        <tr key={a.tradeDate} className="border-t border-line">
                          <td className="px-3 py-2 num text-ink-2">{a.tradeDate}</td>
                          <td className="px-3 py-2 num text-right text-ink-1">{a.tradeCount}</td>
                          <td className={cn('px-3 py-2 num text-right', pnl > 0 ? 'text-up' : pnl < 0 ? 'text-down' : 'text-ink-1')}>
                            {fmtMoney(a.realizedPnl, { sign: true })}
                          </td>
                          <td className="px-3 py-2 num text-right text-ink-1">{fmtMoney(a.tradingCost)}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1">
                              {a.dimensions.length === 0 ? (
                                <span className="text-ink-3 text-xs">未启用维度</span>
                              ) : (
                                a.dimensions.map((x) => <DimensionPill key={x.type} d={x} />)
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1">
                              <Pill tone={a.effectivePassed ? 'up' : 'down'}>{a.effectivePassed ? '达标' : '未达标'}</Pill>
                              {a.overridden && (
                                <span title={a.overrideReason ?? '已改判'}>
                                  <Pill tone="warn">已改判</Pill>
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 下方:交易明细 */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-0">
                  交易明细(逐笔结算)
                  <span className="ml-2 text-xs font-normal text-ink-2 num">{d.deals.length} 笔</span>
                </h3>
                <Button variant="outline" size="sm" disabled={d.deals.length === 0} onClick={exportDeals}>
                  <Download className="h-4 w-4" />
                  导出此人 CSV
                </Button>
              </div>
              {d.dealsTruncated && (
                <p className="text-xs text-warn">明细条数超过上限,仅显示最近 5000 笔;完整数据请用 Excel 导出。</p>
              )}
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead className="bg-bg-3 text-ink-2 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-normal">结算时间</th>
                      <th className="px-3 py-2 text-left font-normal">开仓时间</th>
                      <th className="px-3 py-2 text-left font-normal">品种</th>
                      <th className="px-3 py-2 text-left font-normal">方向</th>
                      <th className="px-3 py-2 text-right font-normal">手数</th>
                      <th className="px-3 py-2 text-right font-normal">开仓价</th>
                      <th className="px-3 py-2 text-right font-normal">平仓价</th>
                      <th className="px-3 py-2 text-right font-normal">毛盈亏</th>
                      <th className="px-3 py-2 text-right font-normal">服务费</th>
                      <th className="px-3 py-2 text-right font-normal">净额</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.deals.length === 0 && (
                      <tr>
                        <td colSpan={10} className="px-3 py-6 text-center text-ink-3">当月无结算记录</td>
                      </tr>
                    )}
                    {d.deals.map((x) => {
                      const net = moneyToNumber(x.netPnl);
                      return (
                        <tr key={x.dealId} className="border-t border-line">
                          <td className="px-3 py-2 num text-ink-2">{fmtDateTime(x.closedAt)}</td>
                          <td className="px-3 py-2 num text-ink-3">{x.openedAt ? fmtDateTime(x.openedAt) : '—'}</td>
                          <td className="px-3 py-2 text-ink-1">{x.symbolName ?? x.symbolId}</td>
                          <td className={cn('px-3 py-2', x.side === 'BUY' ? 'text-up' : 'text-down')}>
                            {x.side === 'BUY' ? '买入' : '卖出'}
                          </td>
                          <td className="px-3 py-2 num text-right text-ink-1">{(x.volume / 100).toFixed(2)}</td>
                          <td className="px-3 py-2 num text-right text-ink-2">{x.entryPrice ?? '—'}</td>
                          <td className="px-3 py-2 num text-right text-ink-2">{x.closePrice ?? '—'}</td>
                          <td className="px-3 py-2 num text-right text-ink-1">{fmtMoney(x.grossPnl, { sign: true })}</td>
                          <td className="px-3 py-2 num text-right text-ink-2">{fmtMoney(x.serviceFee)}</td>
                          <td className={cn('px-3 py-2 num text-right font-medium', net >= 0 ? 'text-up' : 'text-down')}>
                            {fmtMoney(x.netPnl, { sign: true })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {d.deals.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-line bg-bg-2">
                        <td className="px-3 py-2 text-ink-2" colSpan={7}>
                          合计(报表口径)
                        </td>
                        <td className="px-3 py-2 num text-right text-ink-1">{fmtMoney(d.summary.grossPnl, { sign: true })}</td>
                        <td className="px-3 py-2 num text-right text-ink-2">{fmtMoney(d.summary.serviceFee)}</td>
                        <td className="px-3 py-2 num text-right font-semibold text-ink-0">
                          {fmtMoney(d.summary.realizedNet, { sign: true })}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </section>

            {/* 底部:发放决定 */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-ink-0">发放决定</h3>
              <PayoutEditor
                userId={d.user.userId}
                year={year}
                month={month}
                initial={{
                  status: d.payout.status,
                  paidAmount: d.payout.paidAmount,
                  note: d.payout.note,
                }}
                defaultAmount={d.summary.share}
              />
              {d.payout.decidedAt && (
                <p className="text-xs text-ink-3">
                  最近决定:{d.payout.decidedBy ?? '—'} · {fmtDateTime(d.payout.decidedAt)}
                  {d.payout.computedShare && ` · 当时系统计算 ${fmtMoney(d.payout.computedShare)}`}
                </p>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, sub, tone = 'ink' }: { label: string; value: string; sub?: string; tone?: 'ink' | 'up' | 'down' | 'brand' }) {
  const cls = tone === 'up' ? 'text-up' : tone === 'down' ? 'text-down' : tone === 'brand' ? 'text-brand' : 'text-ink-0';
  return (
    <div className="rounded-md border border-line bg-bg-2 px-3 py-2">
      <div className="text-xs text-ink-2">{label}</div>
      <div className={cn('text-lg font-bold num', cls)}>{value}</div>
      {sub && <div className="text-xs text-ink-3 num mt-0.5">{sub}</div>}
    </div>
  );
}
